"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_STYLE_SCOPE_CLASS = exports.STYLE_CLASS_PREFIX = void 0;
exports.getStyleClassName = getStyleClassName;
exports.renderStyleCss = renderStyleCss;
exports.STYLE_CLASS_PREFIX = 'sl-custom-app-style';
exports.DEFAULT_STYLE_SCOPE_CLASS = 'sl-custom-app';
const SURFACE_DECLARATIONS = {
    quiet: 'background: transparent; border-color: transparent;',
    raised: 'background: var(--sl-custom-app-surface-raised, #ffffff); border-color: var(--sl-custom-app-border-subtle, rgba(0, 0, 0, 0.08));',
    editorial: 'background: var(--sl-custom-app-surface-editorial, #f8fafc); border-color: var(--sl-custom-app-border-subtle, rgba(0, 0, 0, 0.08));',
    form: 'background: var(--sl-custom-app-surface-form, #ffffff); border-color: var(--sl-custom-app-border-strong, rgba(0, 0, 0, 0.12));',
    inset: 'background: var(--sl-custom-app-surface-inset, #f4f4f5); border-color: transparent;',
};
const DENSITY_DECLARATIONS = {
    compact: 'padding: 12px; gap: 8px;',
    standard: 'padding: 18px; gap: 12px;',
    spacious: 'padding: 28px; gap: 18px;',
};
const ELEVATION_DECLARATIONS = {
    none: 'box-shadow: none;',
    sm: 'box-shadow: var(--sl-custom-app-shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.08));',
    md: 'box-shadow: var(--sl-custom-app-shadow-md, 0 8px 24px rgba(0, 0, 0, 0.12));',
};
const RADIUS_DECLARATIONS = {
    none: 'border-radius: 0;',
    sm: 'border-radius: 4px;',
    md: 'border-radius: 8px;',
};
const TOKEN_DECLARATIONS = {
    surface: SURFACE_DECLARATIONS,
    padding: {
        compact: 'padding: 12px;',
        standard: 'padding: 18px;',
        spacious: 'padding: 28px;',
    },
    gap: {
        compact: 'gap: 8px;',
        standard: 'gap: 12px;',
        spacious: 'gap: 18px;',
        generous: 'gap: 24px;',
    },
    radius: RADIUS_DECLARATIONS,
    shadow: ELEVATION_DECLARATIONS,
    mediaRatio: {
        none: '--sl-custom-app-media-ratio: auto;',
        thumbnail: '--sl-custom-app-media-ratio: 4 / 3;',
        cover: '--sl-custom-app-media-ratio: 16 / 9;',
        wide: '--sl-custom-app-media-ratio: 21 / 9;',
    },
    fontScale: {
        compact: '--sl-custom-app-font-scale: 0.92;',
        body: '--sl-custom-app-font-scale: 1;',
        display: '--sl-custom-app-font-scale: 1.18;',
    },
    ctaRadius: {
        link: '--sl-custom-app-cta-radius: 0;',
        button: '--sl-custom-app-cta-radius: 6px;',
        quiet: '--sl-custom-app-cta-radius: 4px;',
    },
};
function getStyleClassName(spec) {
    return `${exports.STYLE_CLASS_PREFIX}-${hashStyleId(spec.id)}`;
}
function renderStyleCss(presets, variants = [], options = {}) {
    const scopeClass = sanitizeScopeClass(options.scopeClass ?? exports.DEFAULT_STYLE_SCOPE_CLASS);
    const classMap = {};
    const rules = [];
    const presetIds = new Set(presets.map(preset => preset.id));
    for (const preset of presets) {
        classMap[preset.id] = getStyleClassName(preset);
        rules.push(renderPresetRule(preset, scopeClass));
    }
    for (const variant of variants) {
        if (!presetIds.has(variant.presetId))
            continue;
        const rule = renderVariantRule(variant, scopeClass, classMap, {
            builderViewport: options.builderViewport,
            builderReducedMotion: options.builderReducedMotion,
        });
        if (rule)
            rules.push(rule);
    }
    return { css: rules.join('\n\n').trim(), classMap };
}
function renderPresetRule(preset, scopeClass) {
    const className = getStyleClassName(preset);
    const declarations = [
        'display: flex;',
        'flex-direction: column;',
        'border-style: solid;',
        'border-width: 1px;',
        SURFACE_DECLARATIONS[preset.surface],
        DENSITY_DECLARATIONS[preset.density],
        ELEVATION_DECLARATIONS[preset.elevation],
        RADIUS_DECLARATIONS[preset.radius],
        TOKEN_DECLARATIONS.mediaRatio[preset.mediaTreatment],
        TOKEN_DECLARATIONS.fontScale[preset.typographyScale],
        TOKEN_DECLARATIONS.ctaRadius[preset.ctaTreatment],
        renderRhythmDeclaration(preset.contentRhythm),
    ].filter((value) => Boolean(value));
    return `.${scopeClass} .${className} { ${declarations.join(' ')} }`;
}
function renderVariantRule(variant, scopeClass, classMap, builderOptions) {
    const className = classMap[variant.presetId];
    if (!className)
        return '';
    const state = renderStateSelector(variant.state ?? null);
    const declarations = renderTokenDeclarations(variant.tokens);
    return renderMediaWrapper(variant.media, scopeClass, className, state, declarations, builderOptions);
}
function renderTokenDeclarations(tokens) {
    return Object.entries(tokens)
        .map(([name, value]) => TOKEN_DECLARATIONS[name][value])
        .filter((declaration) => Boolean(declaration))
        .join(' ');
}
function renderStateSelector(state) {
    if (state === 'hover')
        return ':hover';
    if (state === 'focus')
        return ':focus-visible';
    if (state === 'active')
        return ':active';
    return '';
}
function renderMediaWrapper(media, scopeClass, className, state, declarations, builderOptions) {
    const { builderViewport, builderReducedMotion } = builderOptions;
    const baseSelector = `.${scopeClass} .${className}${state}`;
    const builderScopedSelector = (attr, value) => `.${scopeClass}[data-${attr}="${value}"] .${className}${state}`;
    if (media === 'reduced-motion') {
        if (builderReducedMotion === false) {
            return '';
        }
        if (builderReducedMotion === true) {
            return `${baseSelector} { ${declarations} }`;
        }
        return `@media (prefers-reduced-motion: reduce) { ${baseSelector} { ${declarations} } }`;
    }
    if (media === 'mobile' || media === 'desktop') {
        if (builderViewport !== undefined) {
            if (media !== builderViewport) {
                return '';
            }
            return `${builderScopedSelector('builder-viewport', media)} { ${declarations} }`;
        }
        if (media === 'mobile') {
            return `@media (max-width: 720px) { ${baseSelector} { ${declarations} } }`;
        }
        return `@media (min-width: 960px) { ${baseSelector} { ${declarations} } }`;
    }
    return `${baseSelector} { ${declarations} }`;
}
function renderRhythmDeclaration(rhythm) {
    if (rhythm === 'tight')
        return '--sl-custom-app-content-rhythm: 0.85;';
    if (rhythm === 'generous')
        return '--sl-custom-app-content-rhythm: 1.2;';
    return '--sl-custom-app-content-rhythm: 1;';
}
function sanitizeScopeClass(scopeClass) {
    if (!/^[a-z][a-z0-9-]*$/i.test(scopeClass)) {
        throw new Error(`Invalid style scope class: ${scopeClass}`);
    }
    return scopeClass;
}
function hashStyleId(value) {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
}
